import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Avatar,
  List as MUIList,
  ListItemIcon,
  ListItemText as _ListItemText,
  MenuItem as _MenuItem,
} from '@material-ui/core';
import { ClassAttributes, createElement, SyntheticEvent } from 'react';

import TiktokAdAccount from '../../../entity/TiktokAdAccount';
import theme from '../../../theme';
import { SPACING_REGULAR } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import mq from '../../styleUtility/mediaquery';

interface Props {
  tiktokAdAccounts: ReadonlyArray<TiktokAdAccount>;
  selected: TiktokAdAccount | null;
  onClick: (e: SyntheticEvent<HTMLElement>, selected: TiktokAdAccount) => void;
  className?: string;
}

const TiktokAdAccountList = ({
  tiktokAdAccounts,
  selected,
  onClick,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const selectedListItemPrimaryStyle = css({
    color: theme.palette.primary.main,
  }).toString();

  const selectedListItemSecondaryStyle = css({
    color: theme.palette.primary.light,
  }).toString();

  const accounts = tiktokAdAccounts.map((adAccount) => {
    const isSelected = selected ? selected.adAccountId === adAccount.adAccountId : false;

    const adAccountName = adAccount.name ? adAccount.name : 'Tiktok Ads Account';
    const thumbnail = (
      <ListItemIcon>
        <Avatar>{adAccountName.toUpperCase().charAt(0)}</Avatar>
      </ListItemIcon>
    );

    const Account = isSelected ? (
      <LiteItemTextWrapper>
        <ListItemText
          primary={`${adAccountName}`}
          classes={{
            primary: selectedListItemPrimaryStyle,
            secondary: selectedListItemSecondaryStyle,
          }}
        />
        {adAccount.adAccountId ? <ListItemTextId>{adAccount.adAccountId}</ListItemTextId> : null}
      </LiteItemTextWrapper>
    ) : (
      <LiteItemTextWrapper>
        <ListItemText primary={`${adAccountName}`} />
        {adAccount.adAccountId ? <ListItemTextId>{adAccount.adAccountId}</ListItemTextId> : null}
      </LiteItemTextWrapper>
    );

    return (
      <MenuItem selected={isSelected} key={adAccount.id} onClick={(e) => onClick(e, adAccount)}>
        {thumbnail}
        {Account}
      </MenuItem>
    );
  });

  return <Root className={className}>{accounts}</Root>;
};

const Root = styled(MUIList)({
  height: '100%',
  maxHeight: '635px',
  overflowY: 'scroll',
  border: `1px solid ${theme.palette.grey['200']}`,
  [mq('sp')]: {
    maxHeight: 'none',
    overflowY: 'auto',
  },
});

const MenuItem = styled(_MenuItem)(
  flexibleLayoutContainer({
    direction: 'row',
    wrap: 'nowrap',
    alignItems: 'center',
  }),
  {
    [mq('sp')]: {
      height: 'auto',
    },
  },
);

const LiteItemTextWrapper = styled('div')({
  textAlign: 'left',
});

const ListItemText = styled(_ListItemText)({
  marginRight: SPACING_REGULAR,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginBottom: 0,
  [mq('sp')]: {
    whiteSpace: 'initial',
    wordBreak: 'break-word',
    marginRight: 0,
    paddingRight: 0,
    '& > span': {
      lineHeight: 1.1,
    },
    '& > p': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
});

const ListItemTextId = styled('span')({
  fontSize: '0.8rem',
  color: 'rgba(0, 0, 0, 0.54)',
});

export default TiktokAdAccountList;
